/**
 * LookupSuggestionDropdown — Tier-2 portfolio filter: values within a selected
 * entity type, with a dedicated search box, Back control, and colour tags.
 */

import React, { useEffect, useRef } from 'react';
import Search from '../../../atoms/Icon/Search';
import Rollback from '../../../atoms/Icon/Rollback';
import { MatchRange, ScoredMatch } from './lookupSearchAlgo';
import { formatColumnTag, getColumnTagStyleForName } from './lookupEntityTypes';

export interface LookupSuggestionDropdownProps {
    suggestions: ScoredMatch[];
    highlightIndex: number;
    onHighlight: (index: number) => void;
    onSelect: (index: number) => void;
    categoryLabel?: string | null;
    query?: string;
    onQueryChange?: (query: string) => void;
    onSearchKeyDown?: (event: React.KeyboardEvent<HTMLInputElement>) => void;
    onBack?: () => void;
    className?: string;
    'data-test-id'?: string;
}

const DROPDOWN_MAX_HEIGHT = 280;

function renderHighlighted(text: string, ranges: MatchRange[]): React.ReactNode {
    if (!ranges || ranges.length === 0) return text;
    const parts: React.ReactNode[] = [];
    let cursor = 0;
    const sorted = [...ranges].sort((a, b) => a.start - b.start);
    for (let i = 0; i < sorted.length; i++) {
        const r = sorted[i];
        const start = Math.max(r.start, cursor);
        const end = Math.min(r.end, text.length);
        if (start > cursor) {
            parts.push(<span key={`p-${i}`}>{text.slice(cursor, start)}</span>);
        }
        if (end > start) {
            parts.push(
                <span
                    key={`m-${i}`}
                    className="font-semibold text-[#3B2C77] bg-[#f1edff] rounded-sm"
                >
                    {text.slice(start, end)}
                </span>,
            );
            cursor = end;
        }
    }
    if (cursor < text.length) {
        parts.push(<span key="tail">{text.slice(cursor)}</span>);
    }
    return <>{parts}</>;
}

const LookupSuggestionDropdown: React.FC<LookupSuggestionDropdownProps> = ({
    suggestions,
    highlightIndex,
    onHighlight,
    onSelect,
    categoryLabel,
    query = '',
    onQueryChange,
    onSearchKeyDown,
    onBack,
    className,
    'data-test-id': dataTestId,
}) => {
    const scrollContainerRef = useRef<HTMLDivElement>(null);
    const itemRefs = useRef<Array<HTMLDivElement | null>>([]);
    const searchInputRef = useRef<HTMLInputElement>(null);

    const firstItemKey = suggestions[0]
        ? `${suggestions[0].item.column_name}::${suggestions[0].item.entity_name}`
        : '';
    const hasNoMatches = suggestions.length === 0 && query.trim().length > 0;
    const tagStyle = categoryLabel ? getColumnTagStyleForName(categoryLabel) : null;
    const tagLabel = categoryLabel ? formatColumnTag(categoryLabel) : null;

    useEffect(() => {
        searchInputRef.current?.focus();
    }, [categoryLabel]);

    useEffect(() => {
        if (scrollContainerRef.current) {
            scrollContainerRef.current.scrollTop = 0;
        }
    }, [firstItemKey, categoryLabel, query]);

    useEffect(() => {
        const el = itemRefs.current[highlightIndex];
        if (el) el.scrollIntoView({ block: 'nearest' });
    }, [highlightIndex, firstItemKey]);

    const searchPlaceholder = categoryLabel
        ? `Search ${categoryLabel.toLowerCase()}`
        : 'Search entities';

    const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        // Backspace on an empty search box returns to tier 1.
        if (e.key === 'Backspace' && query.length === 0 && onBack) {
            e.preventDefault();
            onBack();
            return;
        }
        onSearchKeyDown?.(e);
    };

    return (
        <div
            className={`absolute bottom-full left-0 right-0 mb-2 z-50 bg-white rounded-[12px] border border-[#e8eafa] shadow-[0px_2.5px_20px_-2.5px_rgba(164,99,243,0.32)] overflow-hidden flex flex-col max-h-[min(520px,70vh)] ${
                className ?? ''
            }`}
            role="listbox"
            aria-label={categoryLabel ? `${categoryLabel} entities` : 'Entity suggestions'}
            data-test-id={dataTestId}
            data-portfolio-filter
            onMouseDown={(e) => {
                if ((e.target as HTMLElement).closest('[data-lookup-search-input]')) return;
                e.preventDefault();
            }}
        >
            <div className="flex items-center gap-2 px-3 pt-3 pb-2 border-b border-[rgba(0,0,0,0.06)] shrink-0">
                {onBack && (
                    <button
                        type="button"
                        className="inline-flex items-center gap-1 shrink-0 bg-transparent border-0 p-0 cursor-pointer font-['Open_Sans'] text-[12px] leading-4 text-[rgba(0,0,0,0.65)] hover:text-[rgba(0,0,0,0.88)]"
                        onClick={onBack}
                        data-test-id={dataTestId ? `${dataTestId}-back` : undefined}
                    >
                        <Rollback size={14} color="rgba(0,0,0,0.65)" />
                        Back
                    </button>
                )}
                {categoryLabel && tagStyle && tagLabel && (
                    <span
                        className="font-['Open_Sans'] font-semibold text-[10px] leading-4 tracking-[0.04em] rounded-md px-2 py-0.5 shrink-0 whitespace-nowrap uppercase"
                        style={{ backgroundColor: tagStyle.bg, color: tagStyle.text }}
                    >
                        {tagLabel}
                    </span>
                )}
            </div>

            <div className="px-3 py-2.5 border-b border-[rgba(0,0,0,0.06)] shrink-0">
                <div className="flex items-center gap-2 rounded-[10px] border border-[#7C3AED] bg-[#faf8ff] px-3 py-2.5">
                    <Search size={16} color="rgba(0,0,0,0.45)" />
                    <input
                        ref={searchInputRef}
                        type="text"
                        value={query}
                        onChange={(e) => onQueryChange?.(e.target.value)}
                        onKeyDown={handleSearchKeyDown}
                        placeholder={searchPlaceholder}
                        className="flex-1 min-w-0 font-['Open_Sans'] text-[14px] leading-5 text-[rgba(0,0,0,0.88)] placeholder:text-[rgba(0,0,0,0.45)] border-0 outline-none bg-transparent p-0"
                        data-lookup-search-input
                        data-test-id={dataTestId ? `${dataTestId}-search-input` : undefined}
                        aria-label={searchPlaceholder}
                    />
                </div>
            </div>

            <div
                ref={scrollContainerRef}
                className="overflow-y-auto flex-1 min-h-0"
                style={{ maxHeight: DROPDOWN_MAX_HEIGHT }}
            >
                {hasNoMatches ? (
                    <div
                        className="px-3 py-4 font-['Open_Sans'] text-[14px] leading-5 text-[rgba(0,0,0,0.65)]"
                        data-test-id={dataTestId ? `${dataTestId}-no-matches` : undefined}
                    >
                        No matches found for &ldquo;{query.trim()}&rdquo;.
                    </div>
                ) : suggestions.length === 0 ? (
                    <div className="px-3 py-4 font-['Open_Sans'] text-[14px] leading-5 text-[rgba(0,0,0,0.45)]">
                        No values available.
                    </div>
                ) : (
                    suggestions.map((match, idx) => {
                        const isActive = idx === highlightIndex;
                        const rowTagStyle = getColumnTagStyleForName(match.item.column_name);
                        const rowTagLabel = formatColumnTag(match.item.column_name);
                        return (
                            <div
                                key={`${match.item.column_name}::${match.item.entity_name}::${idx}`}
                                ref={(el) => {
                                    itemRefs.current[idx] = el;
                                }}
                                role="option"
                                aria-selected={isActive}
                                className={`relative flex items-center justify-between gap-3 px-3 py-2.5 cursor-pointer transition-colors ${
                                    isActive
                                        ? 'bg-[#f5f3ff]'
                                        : 'bg-transparent hover:bg-[#fafafa]'
                                }`}
                                onMouseEnter={() => onHighlight(idx)}
                                onClick={() => onSelect(idx)}
                                data-test-id={
                                    dataTestId ? `${dataTestId}-option-${idx}` : undefined
                                }
                            >
                                {isActive && (
                                    <span
                                        className="absolute left-0 top-1 bottom-1 w-[3px] rounded-r-full bg-[#7C3AED]"
                                        aria-hidden
                                    />
                                )}
                                <span className="font-['Open_Sans'] font-normal text-[14px] leading-5 text-[rgba(0,0,0,0.88)] truncate flex-1 min-w-0">
                                    {renderHighlighted(match.item.entity_name, match.ranges)}
                                </span>
                                <span
                                    className="font-['Open_Sans'] font-semibold text-[10px] leading-4 tracking-[0.04em] rounded-md px-2 py-0.5 shrink-0 whitespace-nowrap uppercase"
                                    style={{
                                        backgroundColor: rowTagStyle.bg,
                                        color: rowTagStyle.text,
                                    }}
                                    title={rowTagLabel}
                                >
                                    {rowTagLabel}
                                </span>
                            </div>
                        );
                    })
                )}
            </div>
        </div>
    );
};

export default LookupSuggestionDropdown;
